import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useCatalogProducts } from '@/hooks/useCatalogProducts';
import CategoryFilter from '@/pages/catalog/components/CategoryFilter';
import ProductDetailModal from '@/pages/catalog/components/ProductDetailModal';
import ProductCard from '@/pages/catalog/components/ProductCard';
import CatalogHeader from '@/pages/catalog/components/CatalogHeader';
import type { BranchDirectoryItem, SocialLinks } from '@/pages/catalog/components/CatalogHeader';
import { useCategories } from '@/hooks/useCategories';
import { useActiveOffers } from '@/hooks/useActiveOffers';
import type { BusinessInfo, Product } from '@/lib/types';

const HERO_IMAGE =
  'https://storage.helloreaddy.io/project_files/0c9a9c3e-9011-4ee6-9dd2-90fcbe3bf101/f5184575-4462-4a92-8048-4dbbfddf3027_compressed_Gemini_Generated_Image_xg1sl3xg1sl3xg1s.webp';

// Cantidad de productos por fila deslizable en la vista de móvil.
const MOBILE_PER_ROW = 8;
const MOBILE_IMAGE_CLASS = 'w-full h-36 bg-background-100 overflow-hidden';

export default function CatalogPage() {
  const [info, setInfo] = useState<BusinessInfo | null>(null);
  const [branches, setBranches] = useState<BranchDirectoryItem[]>([]);
  const [social, setSocial] = useState<SocialLinks>({});
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Product | null>(null);
  const { categories, loading: categoriesLoading } = useCategories();
  const { products, total, loading, hasMore, loadMore } = useCatalogProducts(search, categoryId);
  const { offersByProduct } = useActiveOffers(products.map((p) => p.id));

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [infoRes, branchRes, socialRes] = await Promise.all([
          supabase.rpc('catalog_business_info'),
          supabase.rpc('catalog_branches_v2'),
          supabase
            .from('business_info')
            .select('instagram_url, facebook_url, tiktok_url')
            .limit(1)
            .maybeSingle(),
        ]);
        if (!active) return;
        const row = Array.isArray(infoRes.data) && infoRes.data.length > 0 ? infoRes.data[0] : null;
        setInfo((row as unknown as BusinessInfo) ?? null);
        setBranches((branchRes.data as BranchDirectoryItem[]) ?? []);
        if (socialRes.data) {
          setSocial({
            instagram_url: (socialRes.data.instagram_url as string) ?? null,
            facebook_url: (socialRes.data.facebook_url as string) ?? null,
            tiktok_url: (socialRes.data.tiktok_url as string) ?? null,
          });
        }
      } catch {
        /* se mantienen los valores por defecto */
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  // Divide los productos en filas para el carrusel de móvil.
  const mobileRows = useMemo(() => {
    const rows: Product[][] = [];
    for (let i = 0; i < products.length; i += MOBILE_PER_ROW) {
      rows.push(products.slice(i, i + MOBILE_PER_ROW));
    }
    return rows;
  }, [products]);

  const currencySymbol = info?.currency_symbol || 'RD$';

  const whatsappLink = (productName: string) => {
    const number = (info?.whatsapp || '').replace(/\D/g, '');
    if (!number) return null;
    const msg = `Hola, estoy interesado en ${productName}. ¿Podrían darme más información?`;
    return `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="min-h-screen bg-background-50">
      <CatalogHeader info={info} branches={branches} social={social} />

      {/* Hero con imagen de fondo */}
      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src={HERO_IMAGE}
          alt="Fachada de la tienda"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/20 to-black/70" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-6 min-h-[420px] md:min-h-[560px] flex flex-col justify-between py-8 md:py-10">
          <div className="w-full text-center">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white drop-shadow-md">
              Catálogo de productos
            </h1>
            <p className="mt-3 max-w-xl mx-auto text-sm md:text-base text-white/90">
              Explora nuestros productos y contáctanos por WhatsApp si te interesa alguno.
            </p>
          </div>
          <div className="w-full max-w-xl mx-auto relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Busca por nombre, marca, categoría o descripción…"
              className="w-full rounded-full border border-transparent bg-background-50/95 backdrop-blur pl-11 pr-4 py-3 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
            />
          </div>
        </div>
      </section>

      {/* Filtro de categorías */}
      <div className="max-w-6xl mx-auto px-4 md:px-6 mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <CategoryFilter
          categories={categories}
          value={categoryId}
          onChange={setCategoryId}
          loading={categoriesLoading}
        />
        {!loading && (
          <p className="text-sm text-foreground-500">
            Nuestros productos
          </p>
        )}
      </div>

      {/* Productos */}
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 p-16 text-center">
            <i className="ri-store-3-line text-4xl text-foreground-400" />
            <p className="mt-3 text-foreground-600">No hay productos disponibles por ahora.</p>
          </div>
        ) : (
          <>
            {/* Móvil: filas de 3 productos que se deslizan horizontalmente */}
            <div className="sm:hidden space-y-4">
              {mobileRows.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-4 px-4"
                >
                  {row.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      currencySymbol={currencySymbol}
                      offer={offersByProduct[product.id] ?? null}
                      onSelect={setSelected}
                      className="snap-start shrink-0 w-[calc((100%-0.75rem)/2)]"
                      imageClassName={MOBILE_IMAGE_CLASS}
                      priority={rowIndex === 0 && index < 4}
                    />
                  ))}
                </div>
              ))}
            </div>

            {/* Escritorio: cuadrícula normal */}
            <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  currencySymbol={currencySymbol}
                  offer={offersByProduct[product.id] ?? null}
                  onSelect={setSelected}
                  priority={index < 4}
                />
              ))}
            </div>

            {hasMore && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={loadMore}
                  className="inline-flex items-center gap-2 rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                >
                  <i className="ri-arrow-down-line" /> Ver más productos
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Detalle del producto */}
      <ProductDetailModal
        product={selected}
        currencySymbol={currencySymbol}
        whatsappLink={selected ? whatsappLink(selected.name) : null}
        offer={selected ? offersByProduct[selected.id] ?? null : null}
        onClose={() => setSelected(null)}
      />

      {/* Footer */}
      <footer className="border-t border-background-200 bg-background-100 mt-8">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 text-center">
          <p className="font-heading font-semibold text-foreground-950">
            {branches.length > 0
              ? branches.map((b) => b.name).filter(Boolean).join(' / ')
              : info?.name || 'Mi Tienda'}
          </p>
          {info?.address && <p className="mt-1 text-sm text-foreground-600">{info.address}</p>}
          {info?.footer_message && (
            <p className="mt-3 text-sm text-foreground-500 italic">{info.footer_message}</p>
          )}
        </div>
      </footer>
    </div>
  );
}